import { memo, type TransitionEvent, useCallback, useEffect, useState } from "react";
import { FilterBar, type FilterPanelProps } from "./filter-bar";
import {
  CLOSE_DURATION_MS,
  CLOSE_FALLBACK_MS,
  nextPhase,
  OPEN_DURATION_MS,
  OPEN_FALLBACK_MS,
  type PresenceEvent,
  type PresencePhase,
  presenceView,
} from "./filter-panel-presence";
import { FilterSections } from "./filter-sections";

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function"
    ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
    : false;
}

/**
 * Фаза присутствия разделов поверх чистой `nextPhase`. Смена `open` применяется
 * во время рендера, чтобы разделы монтировались в том же кадре, а не на следующем.
 * Начальное `open=true` — сразу раскрыто, без анимации.
 */
function usePanelPresence(open: boolean) {
  const [state, setState] = useState<{ open: boolean; phase: PresencePhase }>({
    open,
    phase: open ? "open" : "closed",
  });
  let phase = state.phase;
  if (state.open !== open) {
    phase = nextPhase(state.phase, open ? "open" : "close", prefersReducedMotion());
    setState({ open, phase });
  }

  const send = useCallback(
    (event: PresenceEvent) =>
      setState((current) => ({ ...current, phase: nextPhase(current.phase, event, false) })),
    [],
  );

  // Два кадра: после первого браузер зафиксировал закрытые стили, и смена
  // `data-state` на втором даёт именно переход, а не мгновенный скачок.
  useEffect(() => {
    if (phase !== "opening") return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => send("frame"));
    });
    // Если кадр не придёт (фоновая вкладка), фаза всё равно завершится.
    const timer = setTimeout(() => send("frame"), OPEN_FALLBACK_MS);
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
      clearTimeout(timer);
    };
  }, [phase, send]);

  // Страховка на случай, если `transitionend` не придёт (вкладка в фоне и т. п.).
  useEffect(() => {
    if (phase !== "closing") return;
    const timer = setTimeout(() => send("timeout"), CLOSE_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [phase, send]);

  return { view: presenceView(phase), send };
}

/**
 * Панель фильтров экрана: строка над таблицей (`filter-bar.tsx`) и под ней,
 * в развёрнутом виде, четыре раздела (`filter-sections.tsx`) — ТЗ 1.97, §110.
 */
export const FilterPanel = memo(function FilterPanel(props: FilterPanelProps) {
  const { filters, onChange, venues, counts, open } = props;
  const { view, send } = usePanelPresence(open);
  const onTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) send("transitionEnd");
  };
  return (
    // `data-filter-bar` — опора e2e на область фильтров: подложки у полосы по
    // макету нет (§88 п. 8), и по классу её больше не найти.
    <section data-filter-bar className="flex flex-col border-b border-border">
      <FilterBar {...props} />
      {view.mounted ? (
        // Длительность задана inline из констант `filter-panel-presence.ts` —
        // единственный источник, классов `duration-*` нет.
        <div
          data-filter-panel-body
          data-state={view.dataState}
          style={{
            transitionDuration: `${view.dataState === "open" ? OPEN_DURATION_MS : CLOSE_DURATION_MS}ms`,
          }}
          inert={view.inert}
          onTransitionEnd={onTransitionEnd}
          className="grid transition-[grid-template-rows,opacity,translate] ease-[cubic-bezier(.2,0,.2,1)] motion-reduce:transition-none data-[state=open]:grid-rows-[1fr] data-[state=open]:translate-y-0 data-[state=open]:opacity-100 data-[state=closed]:-translate-y-1 data-[state=closed]:grid-rows-[0fr] data-[state=closed]:opacity-0"
        >
          <div className="min-h-0 overflow-hidden">
            <FilterSections filters={filters} onChange={onChange} venues={venues} counts={counts} />
          </div>
        </div>
      ) : null}
    </section>
  );
});
